import classnames from 'classnames'
import PropTypes from 'prop-types'
import {useMutation} from '@apollo/client/react'
import {useState} from 'react'

import Dialog from '@mui/material/Dialog'
import NewFolderIcon from '@mui/icons-material/CreateNewFolderOutlined'

import {BaseButton} from '../shared/blocks/Buttons'
import {Text} from '../shared/blocks/Texts'

import {CREATE_FOLDER} from '../../graphql/mutations/folders'
import {FOLDERS} from '../../graphql/queries/folders'
import {LOCKED} from '../../graphql/queries/locked'

import './nameDialog.scss'

const UNTITLED = 'Untitled folder'

// Where a file is kept is named by the folders above it, joined by slashes.
const PATH_SEPARATOR = '/'

const NewFolderDialog = ({parent, locked = false, onClose}) => {
  const [name, setName] = useState('')
  const [createFolder, {loading, error}] = useMutation(CREATE_FOLDER, {refetchQueries: [locked ? LOCKED : FOLDERS]})
  const separated = name.includes(PATH_SEPARATOR)

  const handleSubmit = event => {
    event.preventDefault()
    if (separated) return

    createFolder({variables: {input: {name: name.trim() || UNTITLED, parentId: parent?.id ?? null, ...(locked && {locked})}}})
      .then(onClose)
      // Shown from the mutation's own error state below.
      .catch(() => {})
  }

  return (
    <Dialog
      open
      onClose={onClose}
      aria-labelledby='new-folder-title'
      slotProps={{paper: {className: classnames('name-dialog', {locked}), component: 'form', onSubmit: handleSubmit}}}>
      <div className='dialog-head'>
        <NewFolderIcon />
        <h2 className='dialog-title' id='new-folder-title'>New folder</h2>
      </div>
      <Text className='dialog-body'>
        {locked ?
          `Created in ${parent?.name ?? 'Locked'}. It stays sealed with everything else here.` :
          `Created in ${parent?.name ?? 'My Drive'}.`}
      </Text>

      <label className='dialog-field'>
        <span className='field-label'>Folder name</span>
        <input
          type='text'
          value={name}
          placeholder={UNTITLED}
          autoComplete='off'
          autoFocus
          onChange={event => setName(event.target.value)}
        />
      </label>
      {separated && <Text className='dialog-error' role='alert'>Folder names can’t contain “{PATH_SEPARATOR}”.</Text>}
      {error && !separated && <Text className='dialog-error' role='alert'>That folder couldn’t be created. Try another name.</Text>}

      <div className='dialog-actions'>
        <BaseButton className='dialog-cancel' variant='text' onClick={onClose}>Cancel</BaseButton>
        <BaseButton className='dialog-create' type='submit' disabled={loading || separated}>Create</BaseButton>
      </div>
    </Dialog>
  )
}

NewFolderDialog.propTypes = {
  // The top of the drive, or of Locked, when absent.
  parent: PropTypes.shape({id: PropTypes.string.isRequired, name: PropTypes.string.isRequired}),
  // In Locked, which must be open.
  locked: PropTypes.bool,
  onClose: PropTypes.func.isRequired
}

export default NewFolderDialog
